import { FunctionComponent, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";

import { ContentStatus, PainPoint } from "../schema/knowledgeDb";

interface PainPointsTableProps {
	rows: PainPoint[];
	onSave?: (rows: PainPoint[]) => void;
	onDelete?: () => void;
	contentStatus?: ContentStatus;
	onApprove?: () => void;
	onUnapprove?: () => void;
}

interface TableForm {
	rows: PainPoint[];
}

export const PainPointsTable: FunctionComponent<PainPointsTableProps> = ({
	rows,
	onSave,
	onDelete,
	contentStatus,
	onApprove,
	onUnapprove,
}) => {
	const [isEditing, setIsEditing] = useState(false);

	const { control, register, handleSubmit, reset } = useForm<TableForm>({
		defaultValues: { rows },
	});

	const { fields, append, remove } = useFieldArray({ control, name: "rows" });

	const handleEdit = () => {
		reset({ rows });
		setIsEditing(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		reset({ rows });
	};

	const onSubmit = (data: TableForm) => {
		onSave?.(data.rows);
		setIsEditing(false);
		if (contentStatus && contentStatus !== ContentStatus.HUMAN_APPROVED) {
			onApprove?.();
		}
	};

	return (
		<div className="rounded-xl border border-gray-300 bg-white mb-4">
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
				<div className="flex items-center gap-2">
					<h3 className="text-sm font-semibold text-gray-900">Pain Points</h3>
					{contentStatus === ContentStatus.AI_DRAFTED && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-white border border-blue-action/40 text-blue-action">
							✨ AI
						</span>
					)}
					{contentStatus === ContentStatus.DRAFT && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-warning-light border border-status-warning/40 text-status-warning">
							✎ Draft
						</span>
					)}
					{contentStatus === ContentStatus.HUMAN_APPROVED && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-success-light border border-status-success/40 text-status-success">
							✓ Approved
						</span>
					)}
				</div>
				{!isEditing && (
					<div className="flex items-center gap-2">
						{onApprove &&
							(contentStatus === ContentStatus.AI_DRAFTED ||
								contentStatus === ContentStatus.DRAFT) && (
								<button
									className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={onApprove}
								>
									✓ Approve
								</button>
							)}
						{onUnapprove && contentStatus === ContentStatus.HUMAN_APPROVED && (
							<button
								className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onUnapprove}
							>
								✗ Unapprove
							</button>
						)}
						{onSave && (
							<button
								className="text-xs font-medium cursor-pointer text-text-meta hover:text-blue-action px-2 py-1 rounded transition-colors"
								type="button"
								onClick={handleEdit}
							>
								Edit
							</button>
						)}
						{onDelete && (
							<button
								className="text-xs font-medium cursor-pointer text-red-500 hover:text-red-500/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onDelete}
							>
								Delete
							</button>
						)}
					</div>
				)}
			</div>

			<div className="px-4 py-3">
				{isEditing ? (
					<form onSubmit={handleSubmit(onSubmit)}>
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-gray-200">
									<th className="text-left py-2 pr-2 font-medium text-gray-700">Title</th>
									<th className="text-left py-2 pr-2 font-medium text-gray-700">Obvious Impact</th>
									<th className="text-left py-2 pr-2 font-medium text-gray-700">Hidden Impact</th>
									<th className="w-8"></th>
								</tr>
							</thead>
							<tbody>
								{fields.map((field, index) => (
									<tr key={field.id} className="border-b border-gray-100">
										<td className="py-2 pr-2">
											<input
												className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
												{...register(`rows.${index}.title`)}
											/>
										</td>
										<td className="py-2 pr-2">
											<input
												className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
												{...register(`rows.${index}.obviousImpact`)}
											/>
										</td>
										<td className="py-2 pr-2">
											<input
												className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
												{...register(`rows.${index}.hiddenImpact`)}
											/>
										</td>
										<td className="py-2">
											<button
												className="text-red-500 hover:text-red-500/80 text-xs cursor-pointer"
												type="button"
												onClick={() => remove(index)}
											>
												✕
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
						<div className="flex justify-between mt-3">
							<button
								className="text-xs font-medium cursor-pointer text-brand-light hover:text-blue-action transition-colors"
								type="button"
								onClick={() =>
									append({ id: Date.now(), title: "", obviousImpact: "", hiddenImpact: "" })
								}
							>
								+ Add row
							</button>
							<div className="flex gap-2">
								<button
									className="px-3 py-1.5 text-xs font-medium cursor-pointer text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
									type="button"
									onClick={handleCancel}
								>
									Cancel
								</button>
								<button
									className="px-3 py-1.5 text-xs font-medium cursor-pointer text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors"
									type="submit"
								>
									Save
								</button>
							</div>
						</div>
					</form>
				) : (
					<ReadOnlyPainPoints rows={rows} />
				)}
			</div>
		</div>
	);
};

const ReadOnlyPainPoints: FunctionComponent<{ rows: PainPoint[] }> = ({ rows }) =>
	rows.length === 0 ? (
		<span className="text-gray-400 italic">No pain points added yet.</span>
	) : (
		<div className="space-y-3">
			{rows.map((pp) => (
				<div key={pp.id} className="rounded-lg border border-gray-200 p-3">
					<div className="font-medium text-gray-900 text-sm mb-2">{pp.title}</div>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<span className="text-xs font-semibold text-status-warning">Obvious Impact</span>
							<p className="text-sm text-text-meta mt-0.5">{pp.obviousImpact}</p>
						</div>
						<div>
							<span className="text-xs font-semibold text-text-meta">Hidden Impact</span>
							<p className="text-sm text-text-meta mt-0.5">{pp.hiddenImpact}</p>
						</div>
					</div>
				</div>
			))}
		</div>
	);
